import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useOutrightPreviewMutation,
  useOutrightSaveMutation,
  type OutrightRowInput,
} from '@/store/api/procurementApi'
import { useLazySuggestXnttNoQuery } from '@/store/api/procurementApi'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
} from '../purchase/dialogStyle'

// ---------------------------------------------------------------------------
// Props — exact signature for orchestrator wiring (SPEC C2).
// Guard (contract_type='Outright', P-*, not forced close) is done by the
// caller before opening — the dialog does not re-check it.
// ---------------------------------------------------------------------------
interface Props {
  open: boolean
  contractNo: string
  /** Raw text of the parent screen's "Requested FX" box; parsed here, BE falls back to fixed FX. */
  requestedFx: string
  onClose: () => void
  onSaved: () => void
}

/** One editable grid row — Access `public_tmp_addmany_pay_outright` datasheet. */
interface EditRow {
  payment_type: string
  truck_id: string
  bank_name: string
  quantity_kg: string
  advanced_vnd: string
  advanced_sub_vnd: string
  pct_pay: string
  description: string
  vat_amount_vnd: string
}

const EMPTY_ROW: EditRow = {
  payment_type: 'Outright',
  truck_id: '',
  bank_name: '',
  quantity_kg: '',
  advanced_vnd: '',
  advanced_sub_vnd: '',
  pct_pay: '',
  description: '',
  vat_amount_vnd: '',
}

const PAYMENT_TYPE_OPTIONS = ['Outright']

/** Parse "1,234.5" → number | null. */
const parseNum = (s: string): number | null => {
  const t = s.replace(/,/g, '').trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** Format numeric — thousands sep, up to 2 decimals (Access 'Standard'). */
const fmtNum = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

/** Row is blank when every user-typed cell (besides the fixed Payment Type) is empty. */
const isRowEmpty = (r: EditRow): boolean =>
  !r.truck_id.trim() &&
  !r.bank_name.trim() &&
  !r.quantity_kg.trim() &&
  !r.advanced_vnd.trim() &&
  !r.advanced_sub_vnd.trim() &&
  !r.pct_pay.trim() &&
  !r.description.trim() &&
  !r.vat_amount_vnd.trim()

const toInputRow = (r: EditRow): OutrightRowInput => ({
  payment_type: r.payment_type || null,
  truck_id: r.truck_id.trim() || null,
  bank_name: r.bank_name.trim() || null,
  quantity_kg: parseNum(r.quantity_kg),
  advanced_vnd: parseNum(r.advanced_vnd),
  advanced_sub_vnd: parseNum(r.advanced_sub_vnd),
  pct_pay: parseNum(r.pct_pay),
  description: r.description.trim() || null,
  vat_amount_vnd: parseNum(r.vat_amount_vnd),
})

const PREVIEW_HEADERS = [
  'Date',
  'Contract No',
  'Payment Type',
  'Truck ID',
  'Price',
  'Price Unit',
  'Requested FX',
  'Bank Name',
  'Quantity (KG)',
  'To be Requested',
  'Advanced (VND)',
  'Request (VND)',
  'VAT Amount (VND)',
  'Advanced sub (VND)',
  'Request sub (VND)',
  'Description',
]

/**
 * Contract Detail → "Add Pay Outright" batch dialog — converts Access
 * `frmProcurementQryDetailsAddPayOutright` ("Procurement - Pay (Outright)").
 *
 * Top: read-only contract context + editable XNTT No (pre-filled via the
 * shared suggest endpoint). Middle: editable input grid (one row per payment
 * line). Bottom: "Refresh to Check" preview grid mirroring the Access
 * `ProcurementPay_WriteOutright` query; Save inserts every previewed line.
 */
export default function ContractDetailAddPayOutrightDialog({
  open,
  contractNo,
  requestedFx,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()

  const [runPreview, { data: preview, isLoading: isPreviewing }] = useOutrightPreviewMutation()
  const [runSave, { isLoading: isSaving }] = useOutrightSaveMutation()
  const [suggestXntt] = useLazySuggestXnttNoQuery()

  const [rows, setRows] = useState<EditRow[]>([{ ...EMPTY_ROW }])
  const [xnttNo, setXnttNo] = useState('')
  // Gate spinner riêng cho cụm context read-only (preview lần đầu) — body/grid nhập render ngay.
  const [contextLoading, setContextLoading] = useState(false)

  const parsedFx = useMemo(() => parseNum(requestedFx), [requestedFx])

  // Re-init each time the dialog opens: reset grid, fetch header context
  // (preview with rows=[]) + suggest XNTT No.
  useEffect(() => {
    if (!open || !contractNo) return
    setRows([{ ...EMPTY_ROW }])
    setXnttNo('')
    setContextLoading(true)
    void (async () => {
      try {
        await runPreview({ contract_no: contractNo, requested_fx: parsedFx, rows: [] }).unwrap()
      } catch {
        // preview is best-effort — context stays blank on error
      } finally {
        setContextLoading(false)
      }
    })()
    void (async () => {
      try {
        const sug = await suggestXntt({ contractNo }).unwrap()
        setXnttNo(sug.suggestedNo)
      } catch {
        // suggest is best-effort — user can still type the XNTT No manually
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractNo])

  const payloadRows = (): OutrightRowInput[] =>
    rows.filter((r) => !isRowEmpty(r)).map(toInputRow)

  const handleRefresh = async () => {
    try {
      await runPreview({
        contract_no: contractNo,
        requested_fx: parsedFx,
        rows: payloadRows(),
      }).unwrap()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to refresh preview'))
    }
  }

  const handleSave = async () => {
    try {
      const res = await runSave({
        contract_no: contractNo,
        requested_fx: parsedFx,
        rows: payloadRows(),
        xntt_no: xnttNo.trim() || null,
      }).unwrap()
      snack.success(`Raw records: ${res.raw_records} — Error: ${res.errors}`)
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save'))
    }
  }

  const setRowField = (index: number, field: keyof EditRow, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)))
  }

  const addRow = () => setRows((prev) => [...prev, { ...EMPTY_ROW }])

  const removeRow = (index: number) =>
    setRows((prev) => (prev.length <= 1 ? [{ ...EMPTY_ROW }] : prev.filter((_, i) => i !== index)))

  const roField = (label: string, value: string, numeric = false) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={value}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={numeric ? [numSx, roSx] : roSx}
    />
  )

  const cellInput = (index: number, field: keyof EditRow, numeric = false, width = 110) => (
    <TextField
      size="small"
      value={rows[index][field]}
      onChange={(e) => setRowField(index, field, e.target.value)}
      sx={[{ minWidth: width }, ...(numeric ? [numSx] : [])]}
      disabled={isSaving}
    />
  )

  const previewRows = preview?.rows ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title="Inbound Logistics - Pay (Outright)"
          sx={dialogTitleTextSx}
        >
          Inbound Logistics - Pay (Outright)
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            onClick={() => void handleSave()}
            disabled={isSaving || isPreviewing}
            sx={dialogHeaderBtnSx}
          >
            {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* ----- Contract context (label left / value right, 2 columns) ----- */}
        {contextLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2, mb: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              mb: 1.5,
            }}
          >
            {/* Row 1: Contract No | Date */}
            <Box>{roField('Contract No', contractNo)}</Box>
            <Box>{roField('Date', dayjs().format('DD-MMM-YYYY'))}</Box>

            {/* Row 2: To be Receive | Fixed FX (on Contract) */}
            <Box>{roField('To be Receive', fmtNum(preview?.pending), true)}</Box>
            <Box>{roField('Fixed FX (on Contract)', fmtNum(preview?.fixed_fx), true)}</Box>

            {/* Row 3: Requested FX (resolved by BE) | XNTT No (editable) */}
            <Box>{roField('Requested FX', fmtNum(preview?.requested_fx), true)}</Box>
            <Box>
              <TextField
                label="XNTT No"
                size="small"
                fullWidth
                value={xnttNo}
                onChange={(e) => setXnttNo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={isSaving}
              />
            </Box>

            {/* Row 4: Applicable VAT | Pending.Outright */}
            <Box>{roField('Applicable VAT', preview?.vat_txt ?? '')}</Box>
            <Box>{roField('Pending.Outright', fmtNum(preview?.pending_outright), true)}</Box>
          </Box>
        )}

        <Typography
          sx={{ color: 'error.main', fontStyle: 'italic', fontSize: '0.75rem', mb: 1 }}
        >
          {'* Auto fill Advanced (Contract + TruckID + Quantity) If you input Null Value in "Advanced VND" column'}
        </Typography>

        {/* ----- Input grid (Access subForm: tmp_addmany_pay_outright) ----- */}
        <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.5 }}>
          Input data in the below form
        </Typography>
        <Box sx={{ overflowX: 'auto', mb: 1 }}>
          <Table size="small" sx={{ '& td, & th': { px: 0.5, py: 0.5, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Payment Type</TableCell>
                <TableCell>Truck ID</TableCell>
                <TableCell>Bank Name</TableCell>
                <TableCell align="right">Quantity KG</TableCell>
                <TableCell align="right">Advanced VND</TableCell>
                <TableCell align="right">Advanced sub VND</TableCell>
                <TableCell align="right">% Pay</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">VAT Amount VND</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Autocomplete
                      options={PAYMENT_TYPE_OPTIONS}
                      value={row.payment_type || null}
                      onChange={(_e, v) => setRowField(i, 'payment_type', v ?? '')}
                      disabled={isSaving}
                      size="small"
                      sx={{ minWidth: 130 }}
                      renderInput={(params) => <TextField {...params} size="small" />}
                    />
                  </TableCell>
                  <TableCell>{cellInput(i, 'truck_id', false, 90)}</TableCell>
                  <TableCell>{cellInput(i, 'bank_name', false, 120)}</TableCell>
                  <TableCell>{cellInput(i, 'quantity_kg', true, 100)}</TableCell>
                  <TableCell>{cellInput(i, 'advanced_vnd', true, 110)}</TableCell>
                  <TableCell>{cellInput(i, 'advanced_sub_vnd', true, 110)}</TableCell>
                  <TableCell>{cellInput(i, 'pct_pay', true, 70)}</TableCell>
                  <TableCell>{cellInput(i, 'description', false, 160)}</TableCell>
                  <TableCell>{cellInput(i, 'vat_amount_vnd', true, 110)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => removeRow(i)}
                      disabled={isSaving}
                      aria-label="Remove row"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Button
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={addRow}
          disabled={isSaving}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Add row
        </Button>

        {/* ----- Check section: New Pending.Outright + Refresh + preview ----- */}
        <Divider sx={{ mb: 1.5 }} />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 1 }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', flex: '1 1 auto' }}>
            Check data in the below form before click &quot;SAVE&quot;
          </Typography>
          <Box sx={{ width: { xs: '100%', sm: 220 } }}>
            {roField('New Pending.Outright', fmtNum(preview?.new_pending), true)}
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleRefresh()}
            disabled={isPreviewing || isSaving}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            {isPreviewing ? <CircularProgress size={14} color="inherit" /> : 'Refresh to Check'}
          </Button>
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { px: 0.5, py: 0.5, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                {PREVIEW_HEADERS.map((h) => (
                  <TableCell
                    key={h}
                    align={
                      [
                        'Price',
                        'Requested FX',
                        'Quantity (KG)',
                        'To be Requested',
                        'Advanced (VND)',
                        'Request (VND)',
                        'VAT Amount (VND)',
                        'Advanced sub (VND)',
                        'Request sub (VND)',
                      ].includes(h)
                        ? 'right'
                        : 'left'
                    }
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={PREVIEW_HEADERS.length}>
                    <Typography variant="caption" color="text.secondary">
                      {isPreviewing
                        ? 'Loading…'
                        : 'No preview rows — input data above then click "Refresh to Check".'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                previewRows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.date ? dayjs(r.date).format('DD-MMM-YYYY') : ''}</TableCell>
                    <TableCell>{r.contract_no}</TableCell>
                    <TableCell>{r.payment_type ?? ''}</TableCell>
                    <TableCell>{r.truck_id ?? ''}</TableCell>
                    <TableCell align="right">{fmtNum(r.price)}</TableCell>
                    <TableCell>{r.price_unit ?? ''}</TableCell>
                    <TableCell align="right">{fmtNum(r.requested_fx)}</TableCell>
                    <TableCell>{r.bank_name ?? ''}</TableCell>
                    <TableCell align="right">{fmtNum(r.quantity_kg)}</TableCell>
                    <TableCell align="right">{fmtNum(r.to_be_requested)}</TableCell>
                    <TableCell align="right">{fmtNum(r.advanced_vnd)}</TableCell>
                    <TableCell align="right">{fmtNum(r.request_vnd)}</TableCell>
                    <TableCell align="right">{fmtNum(r.vat_amount_vnd)}</TableCell>
                    <TableCell align="right">{fmtNum(r.advanced_sub_vnd)}</TableCell>
                    <TableCell align="right">{fmtNum(r.request_sub_vnd)}</TableCell>
                    <TableCell>{r.description ?? ''}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
